import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { store } from "../app/store"
import { version } from '../../../package.json'
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import oktaAuthClient, { getWebuid } from '../config/okta.config';

enum ELogTypeAws {
    Error = "Error",
    Warning = "Warning",
    Notice = "Notice",
    Info = "Info",
    Debug = "Debug",
    None = "None",
}

type AwsCredentials = Awaited<ReturnType<ReturnType<typeof fromCognitoIdentityPool>>>
type RumContext = Record<string, string>
// Only flat values, so full objects (and the patient data inside them) never reach RUM.
type RumEventData = Record<string, string | number | boolean>

const { VITE_OKTA_ISSUER, VITE_AWS_REGION, VITE_AWS_RUM_APP_ID, VITE_AWS_COGNITO_IDENTITY_POOL_ID } = import.meta.env

const OIDC_PROVIDER = VITE_OKTA_ISSUER.replace(/^https:\/\//, ""); // match what’s registered in IAM, without https://
const CREDENTIALS_REFRESH_BUFFER_MS = 5 * 60 * 1000
const NONE = "none"
const SCREEN_BY_SEGMENT: Record<string, string> = {
    patients: "patientList",
    overview: "overview",
    records: "records",
    clinical: "treat",
    order: "order",
}

let awsRumClient: AwsRum | undefined = undefined;
let cachedCredentials: AwsCredentials | undefined
let pendingCredentials: Promise<AwsCredentials> | undefined
let lastUserContextKey = ""

// Called by RUM before each signed request; reads the current Okta token so
// renewed tokens are picked up without rebuilding the client.
async function getAwsCredentials(): Promise<AwsCredentials> {
    const expiresAt = cachedCredentials?.expiration?.getTime()
    if (cachedCredentials && (!expiresAt || expiresAt - Date.now() > CREDENTIALS_REFRESH_BUFFER_MS)) {
        return cachedCredentials
    }
    if (!pendingCredentials) {
        pendingCredentials = (async () => {
            const jwt_token = oktaAuthClient.getAccessToken()
            if (!jwt_token) {
                throw new Error("Okta access token is required to get AWS credentials")
            }
            const credentialProvider = fromCognitoIdentityPool({
                clientConfig: { region: VITE_AWS_REGION },
                identityPoolId: VITE_AWS_COGNITO_IDENTITY_POOL_ID,
                logins: {
                    [OIDC_PROVIDER]: jwt_token,
                },
            });
            cachedCredentials = await credentialProvider()
            return cachedCredentials
        })().finally(() => {
            pendingCredentials = undefined
        })
    }
    return pendingCredentials
}

function buildAWSRUM() {
    try {
        const config: AwsRumConfig = {
            sessionSampleRate: 1,
            endpoint: "https://dataplane.rum.us-west-2.amazonaws.com",
            telemetries: [[
                'performance',
                {
                    ignore: (entry: PerformanceEntry) => {
                        return entry.entryType === 'resource';
                    }
                }
            ], "errors", "http"],
            allowCookies: true,
            enableXRay: false,
            sessionAttributes: {
                webUUID: getWebuid(),
                userId: NONE,
                orgId: NONE,
                role: NONE,
                screen: NONE,
                patientId: NONE,
                caseId: NONE,
            },
            signing: true,
            disableAutoPageView: true,
            // Inactivity timeout: each recorded event extends the session.
            sessionLengthSeconds: 1800,
            // Default is 200; once hit, events are dropped until the session expires (up to 30 min).
            sessionEventLimit: 1000
        };

        const APPLICATION_VERSION: string = version;

        const awsRum: AwsRum = new AwsRum(
            VITE_AWS_RUM_APP_ID,
            APPLICATION_VERSION,
            VITE_AWS_REGION,
            config
        );
        awsRum.setAwsCredentials(getAwsCredentials);
        awsRum.enable();
        console.log("AWS RUM web client initialized with Cognito Identity Pool credentials");
        return awsRum;
    } catch (error) {
        console.error("Error initializing CloudWatch RUM web client:", error);
        // Ignore errors thrown during CloudWatch RUM web client initialization
    }
}

function setRumContext(context: RumContext) {
    awsRumClient?.addSessionAttributes(context)
}

// Keeps userId / orgId / role in sync with the store (profile load, org switch, logout).
function syncUserContext() {
    const user = store?.getState()?.userService?.user
    const orgId = user?.current_orgId ? String(user.current_orgId) : NONE
    const role = user?.authorized_roles
        ?.find((r) => String(r.org_id) === orgId)
        ?.roles?.join(",") || NONE
    const userContext: RumContext = {
        userId: user?.user_id ? String(user.user_id) : NONE,
        orgId,
        role,
    }
    const userContextKey = JSON.stringify(userContext)
    if (userContextKey === lastUserContextKey) {
        return
    }
    lastUserContextKey = userContextKey
    setRumContext(userContext)
}

function getRouteContext(pathname: string): RumContext {
    const path = pathname.replace(/^\/org\/[^/]+/, "")
    const segment = path.split("/")[1] || "home"
    let screen = SCREEN_BY_SEGMENT[segment] ?? segment
    if (screen === "records" && /\/rxForm(\/|$)/.test(path)) {
        screen = "rx"
    }
    const patientId =
        path.match(/\/patient\/([^/]+)/)?.[1] ??
        path.match(/^\/patients\/update\/([^/]+)/)?.[1]
    const caseId = path.match(/\/case\/([^/]+)/)?.[1]
    return {
        screen,
        patientId: patientId ?? NONE,
        caseId: caseId ?? NONE,
    }
}

function loadAWSRUMClient(jwt: string): AwsRum | undefined {
    if (VITE_AWS_RUM_APP_ID === undefined || VITE_AWS_RUM_APP_ID.trim() === '') {
        console.info("AWS RUM App ID is not set. Skipping AWS RUM client initialization.");
        return;
    }
    // Build once; token renewals are handled by the credential provider.
    if (awsRumClient) {
        return awsRumClient;
    }
    if (!jwt || jwt.trim() === '') {
        console.info("JWT token is required to load AWS RUM");
        return;
    }
    awsRumClient = buildAWSRUM();
    if (awsRumClient) {
        setRumContext(getRouteContext(window.location.pathname))
        syncUserContext()
        store.subscribe(syncUserContext)
    }
    return awsRumClient;
}

function getAWSRUMClient(): AwsRum | undefined {
    if (!awsRumClient) {
        console.info("AWS RUM client is not initialized. Call loadAWSRUM first.");
        return;
    }
    return awsRumClient;
}

// V8 ("    at fn (url:1:2)") and Firefox/Safari ("fn@url:1:2") frames: code locations only.
const STACK_FRAME_PATTERN = /^\s*at\s.+:\d+:\d+\)?$|^[\w$.<>/]*@\S+:\d+:\d+$/

/**
 * Record a console.error without its text: console arguments and error messages
 * can contain patient data. Sends only the error type and the stack frames.
 */
function recordConsoleError(args: unknown[]) {
    const error = args.find((arg): arg is Error => arg instanceof Error)
    const name = error?.name ?? "ConsoleError"
    const stack = error?.stack
        ?.split("\n")
        .filter((line) => STACK_FRAME_PATTERN.test(line))
        .join("\n")
    awsRumClient?.recordError({ name, message: `console.error: ${name}`, stack })
}

/**
 * Record a custom RUM event. Name it `area.action` (e.g. "case.open").
 * Session context (userId, orgId, patientId, caseId, screen) is attached by RUM.
 */
function trackEvent(name: `${string}.${string}`, data: RumEventData = {}) {
    awsRumClient?.recordEvent(name, data)
}


function useAwsRumRouteTracking() {
  const location = useLocation();

  useEffect(() => {
      setRumContext(getRouteContext(location.pathname))
      awsRumClient?.recordPageView({
        pageId: location.pathname.replace(/([0-9]+)/g, "*"),
        pageAttributes: {
            actualPath: location.pathname
        }
      });
  }, [location.pathname]);
}

function logAws(level: ELogTypeAws, messageData: {}) {
    const awsRumClient = getAWSRUMClient()
    if (!awsRumClient) {
        return;
    }
    const eventType = "ucloud_event";
    // log data
    const logData = {
        level: level,
        eventType: eventType,
        timestamp: new Date().toString(),
        ...messageData,
    };
    // record
    awsRumClient.recordEvent(eventType, logData);
    // switch (level) {
    //     case ELogTypeAws.Error:
    //         // record Error, use recordError
    //         awsRumClient.recordError({
    //             customData: { ...logData },
    //         });
    //         break;
    //     default:
    //         // other level：use recordEvent
    //         awsRumClient.recordEvent(`${level}_${eventType}`, {
    //             customData: { ...logData },
    //         });
    //         break;
    // }
}

export { loadAWSRUMClient, getAWSRUMClient, useAwsRumRouteTracking, logAws, trackEvent, recordConsoleError, getRouteContext, ELogTypeAws};
